import { useMutation } from "@apollo/client/react"
import { Dialog, Transition } from "@headlessui/react"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import cn from "classnames"
import React, { useEffect, useCallback, useState, useRef, type MutableRefObject } from "react"
import { useTranslation } from "react-i18next"
import { MinimaticAlignment, type MinimaticAlignmentConfiguration, type MinimaticAlignmentSign } from "./alignment"
import { MinimaticColorsConfigurer, type MinimaticColorsConfiguration, type MinimaticColorsSign } from "./colors"
import {
  MinimaticFixmethodsConfigurer,
  type MinimaticFixmethodsConfiguration,
  type MinimaticFixmethodsSign,
} from "./fixmethods"
import { MinimaticFontsConfigurer, type MinimaticFontsConfiguration, type MinimaticFontsSign } from "./fonts"
import { graphql } from "@/api"
import Checkbox from "@/common/components/checkbox"
import LoadingAnimation from "@/common/components/loading"
import { basketAddToGA4 } from "@/common/util"
import { setBasketStateDoc } from "@/menu/components/basket_modal"
import { loadMinimatic, type MinimaticSignData, type MinimaticSignState } from "@/signomatic/app/minimatic"
import type Sign from "@/signomatic/app/sign"
import Canvas from "@/signomatic/components/canvas"
import { CloseLarge, CartFill } from "@/skylticons"

interface ModalContextProps {
  open?: boolean
  disableClose?: boolean
  setOpen: (open: boolean) => void
}

const ModalContext = React.createContext<ModalContextProps>({ setOpen: () => null })
const useModalContext = () => React.useContext(ModalContext)

interface ModalTitleProps {
  children?: React.ReactNode
}

const ModalTitle: React.FC<ModalTitleProps> = ({ children }) => {
  const { setOpen } = useModalContext()

  return (
    <div className="flex items-center justify-between border-b border-white-10 px-6 py-4">
      <div>
        <h3 className="mb-0 text-xl font-semibold leading-[30px]">Minimatic configuration</h3>
        <p className="mb-0 text-sm">{children}</p>
      </div>

      <button className="button button-large button-secondary mb-0" onClick={() => setOpen(false)} type="button">
        <CloseLarge />
        <span>Close without saving</span>
      </button>
    </div>
  )
}

interface ModalBodyProps {
  children?: React.ReactNode
}

const ModalBody: React.FC<ModalBodyProps> = ({ children }) => {
  return <div className="overflow-y-auto p-12 text-sm">{children}</div>
}

interface ModalFooterProps {
  children?: React.ReactNode
}

const ModalFooter: React.FC<ModalFooterProps> = ({ children }) => {
  return <footer className="sticky bottom-0 flex justify-end border-t border-white-10 px-8 py-4">{children}</footer>
}

interface ModalProps {
  children?: React.ReactNode
  width?: string
  open?: boolean
  disableClose?: boolean
  setOpen: (open: boolean) => void
  initialFocus?: MutableRefObject<HTMLElement | null>
}

const Modal: React.FC<ModalProps> & {
  Body: typeof ModalBody
  Footer: typeof ModalFooter
  Title: typeof ModalTitle
} = ({ children, width, open, setOpen, disableClose, initialFocus }) => {
  return (
    <Transition appear show={open} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-40"
        open={open}
        onClose={disableClose ? () => null : setOpen}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center px-3 py-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-10"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-10"
            >
              <Dialog.Panel
                className="minimatic-config relative flex max-h-[95vh] transform flex-col overflow-hidden rounded-2xl bg-white text-left shadow-[0px_2px_4px_rgba(20,33,61,0.05),0px_8px_16px_rgba(20,33,61,0.15)] transition-all"
                style={{ width }}
              >
                <ModalContext.Provider value={{ open, disableClose, setOpen }}>{children}</ModalContext.Provider>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

Modal.Title = ModalTitle
Modal.Body = ModalBody
Modal.Footer = ModalFooter

const toggleMinimaticDoc = graphql(`
  mutation SignsExampleToggleMinimatic($id: ID!, $minimatic: Boolean!) {
    exampleUpdate(id: $id, minimatic: $minimatic) {
      example {
        id
        minimatic
      }
    }
  }
`)

const updateMinimaticConfigDoc = graphql(`
  mutation MinimaticConfigUpdate(
    $exampleId: ID!
    $backgroundColors: [String!]
    $backgroundColorsEnabled: Boolean
    $contentColors: [String!]
    $contentColorsEnabled: Boolean
    $fixedColors: [FixedColor!]
    $fonts: [String!]
    $fontsEnabled: Boolean
    $textAlign: TextAlignInput
    $fixmethods: [String!]
    $fixmethodsEnabled: Boolean
  ) {
    minimaticConfigurationUpdate(
      exampleId: $exampleId
      backgroundColors: $backgroundColors
      backgroundColorsEnabled: $backgroundColorsEnabled
      contentColors: $contentColors
      contentColorsEnabled: $contentColorsEnabled
      fixedColors: $fixedColors
      fonts: $fonts
      fontsEnabled: $fontsEnabled
      textAlign: $textAlign
      fixmethods: $fixmethods
      fixmethodsEnabled: $fixmethodsEnabled
    ) {
      example {
        id

        minimaticConfiguration {
          id
          backgroundColors
          backgroundColorsEnabled
          contentColors
          contentColorsEnabled
          fixedColors
          fonts
          fontsEnabled
          fixmethods
          fixmethodsEnabled

          textAlign {
            text1
            text2
            text3
          }
        }
      }
    }
  }
`)

type MinimaticConfig = MinimaticColorsConfiguration &
  MinimaticAlignmentConfiguration &
  MinimaticFontsConfiguration &
  MinimaticFixmethodsConfiguration
type MinimaticSign = MinimaticColorsSign &
  MinimaticAlignmentSign &
  MinimaticFontsSign &
  MinimaticFixmethodsSign & { signTypeNameEn?: string | null }

interface MinimaticConfigProps {
  exampleId: string
  sign?: MinimaticSignState & MinimaticSign
  signomaticData?: MinimaticSignData | null
  allowed?: boolean
  enabled?: boolean
  configuration?: MinimaticConfig | null
  onSave: (text1: string | null | undefined, text2: string | null | undefined, text3: string | null | undefined) => void
  className?: string
}

export const MinimaticConfig: React.FC<MinimaticConfigProps> = ({
  exampleId,
  sign,
  signomaticData,
  allowed,
  enabled,
  configuration,
  onSave,
  className,
}) => {
  const [toggleMutation, toggleResult] = useMutation(toggleMinimaticDoc)
  const [configOpen, setConfigOpen] = useState(false)

  const toggle = useCallback(
    (enable: boolean) => {
      toggleMutation({ variables: { id: exampleId, minimatic: enable } })
    },
    [exampleId, toggleMutation]
  )

  const ssrDisabled = useSSRDisabled()

  const [currentConfig, setCurrentConfig] = useState<MinimaticConfig | undefined>(configuration || undefined)

  useEffect(() => {
    if (configuration && !currentConfig?.textAlign) {
      setCurrentConfig(configuration)
    }
  }, [configuration, currentConfig])

  const closeConfig = () => {
    setConfigOpen(false)
    setCurrentConfig(configuration || undefined)
  }

  const toggleConfig = () => {
    configOpen ? closeConfig() : setConfigOpen(true)
  }

  const updateConfig = (config: Partial<MinimaticConfig>) => {
    setCurrentConfig(current => ({
      ...current,
      ...config,
      textAlign: { ...currentConfig?.textAlign, ...config.textAlign },
    }))
  }

  return (
    <div className={cn("group relative flex items-center [&_.checkbox]:mb-0 [&_.checkbox]:py-0", className)}>
      <Checkbox
        id={`toggle_minimatic_${exampleId}`}
        name={`toggle_minimatic_${exampleId}`}
        disabled={ssrDisabled || toggleResult.loading || !allowed}
        checked={enabled || false}
        label={<span className="sr-only">Toggle Minimatic</span>}
        onChange={e => toggle(e.target.checked)}
        labelClassName="-top-2.5"
        inputClassName="enable-minimatic"
      />

      <button
        className="button button-small button-secondary mb-0"
        disabled={ssrDisabled || !allowed || !enabled}
        type="button"
        onClick={() => {
          setConfigOpen(true)
        }}
      >
        Minimatic Config
      </button>

      {!allowed && (
        <div className="absolute top-full hidden rounded bg-white-1 p-1 text-center font-semibold text-ink-100 group-hover:block">
          Minimatic signs require: Fixing method, at least 1 line of text, no multi-line text.
        </div>
      )}

      {configOpen && (
        <MinimaticConfigModal
          sign={sign}
          exampleId={exampleId}
          signomaticData={signomaticData}
          enabled={enabled}
          open={configOpen}
          setConfigOpen={setConfigOpen}
          updateConfig={updateConfig}
          currentConfig={currentConfig}
          setCurrentConfig={setCurrentConfig}
          toggleOpen={toggleConfig}
          onSave={onSave}
        />
      )}
    </div>
  )
}

interface MinimaticConfigModalProps {
  sign?: MinimaticSignState & MinimaticSign
  exampleId: string
  signomaticData?: MinimaticSignData | null
  enabled?: boolean
  open: boolean
  setConfigOpen: (open: boolean) => void
  updateConfig: (config: Partial<MinimaticConfig>) => void
  currentConfig: MinimaticConfig | undefined
  setCurrentConfig: (config: MinimaticConfig | undefined) => void
  toggleOpen: () => void
  onSave: (text1: string | null | undefined, text2: string | null | undefined, text3: string | null | undefined) => void
}

const MinimaticConfigModal: React.FC<MinimaticConfigModalProps> = ({
  sign,
  exampleId,
  signomaticData,
  enabled,
  open,
  toggleOpen,
  setConfigOpen,
  updateConfig,
  currentConfig,
  onSave,
  setCurrentConfig,
}) => {
  const [minimatic, setMinimatic] = useState<Sign>()
  const [showFixmethodsConfig, setShowFixmethodsConfig] = useState(true)
  const [updateMutation, { loading: updateLoading }] = useMutation(updateMinimaticConfigDoc)

  const handleLoadMinimatic = useCallback(() => {
    async function load(signState: MinimaticSignState, data: MinimaticSignData) {
      const minimatic = await loadMinimatic(signState, data, true)
      setMinimatic(minimatic)

      const fixmethodsThreshold =
        data.signData
          .filter(f => f.name === sign?.signType)
          .map(sign => sign.fixmethods.map(fixmethod => fixmethod.name)).length < 2

      setShowFixmethodsConfig(fixmethodsThreshold)
    }

    if (!minimatic && sign && enabled && signomaticData) {
      load(sign, signomaticData).catch(error => {
        console.error(error)
      })
    }
  }, [enabled, minimatic, sign, signomaticData])

  useEffect(() => {
    handleLoadMinimatic()
  }, [handleLoadMinimatic])

  const saveConfig = () => {
    updateMutation({
      variables: {
        exampleId,
        ...currentConfig,
        textAlign: {
          text1: currentConfig?.textAlign?.text1,
          text2: currentConfig?.textAlign?.text2,
          text3: currentConfig?.textAlign?.text3,
        },
      },
      onCompleted: data => {
        setConfigOpen(false)

        if (data.minimaticConfigurationUpdate?.example.minimaticConfiguration) {
          setCurrentConfig(data.minimaticConfigurationUpdate.example.minimaticConfiguration)
          onSave(currentConfig?.textAlign?.text1, currentConfig?.textAlign?.text2, currentConfig?.textAlign?.text3)
        }
      },
      onError: error => {
        console.error(error)

        if (error.graphQLErrors[0] && error.graphQLErrors.length > 0) {
          return alert(error.graphQLErrors[0].message)
        }

        alert("Error saving configuration.")
      },
    })
  }

  if (!minimatic) {
    return null
  }

  return (
    <Modal width="85vw" open={open} setOpen={toggleOpen} disableClose={updateLoading}>
      <Modal.Title>Sign type: {sign?.signTypeNameEn}</Modal.Title>
      <Modal.Body>
        <div className="space-y-8">
          <div>
            <h4 className="w-auto whitespace-nowrap text-xl leading-6">Text alignment</h4>
            <div className="flex space-x-4">
              <div className="rounded bg-white-10">
                <Canvas sign={minimatic} />
              </div>
              <MinimaticAlignment
                minimatic={minimatic}
                sign={sign}
                textAlignConfig={currentConfig?.textAlign}
                update={updateConfig}
              />
            </div>
          </div>

          <MinimaticFontsConfigurer minimatic={minimatic} config={currentConfig} update={updateConfig} />

          <MinimaticColorsConfigurer minimatic={minimatic} config={currentConfig} sign={sign} update={updateConfig} />

          {showFixmethodsConfig && (
            <MinimaticFixmethodsConfigurer minimatic={minimatic} config={currentConfig} update={updateConfig} />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="button button-large button-primary mb-0"
          disabled={updateLoading}
          onClick={saveConfig}
          type="button"
        >
          Save configuration
        </button>
      </Modal.Footer>
    </Modal>
  )
}

const minimaticBasketAddDoc = graphql(`
  mutation MinimaticBasketAdd($product: String!) {
    basketAdd(product: $product, example: true) {
      success
      landingUrl

      item {
        id
        priceSek
        totalCount
        sizeDisplay
        hangType
        materialNameForGtm
        signTypeKey
        discountPercentage
        exampleId
      }

      status {
        ...BasketModal_Order
      }
    }
  }
`)

interface MinimaticAddButtonProps {
  signId: string
  disabled?: boolean
  minimatic?: Sign
  count?: number
  signCategoryId?: number
  examplerId?: number
  setFixmethodPickerOpen?: (open: boolean) => void
}

export const MinimaticAddButton: React.FC<MinimaticAddButtonProps> = ({
  signId,
  disabled,
  minimatic,
  count,
  examplerId,
  signCategoryId,
  setFixmethodPickerOpen,
}) => {
  const { t } = useTranslation()
  const warningShown = useRef(false)

  const [setBasketState] = useMutation(setBasketStateDoc)

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: true } })
  }, [setBasketState])

  const [mutation, result] = useMutation(minimaticBasketAddDoc, {
    update: (store, { data: basketAddData }) => {
      if (basketAddData?.basketAdd?.success && basketAddData.basketAdd.item) {
        const orderItem = basketAddData.basketAdd.item
        const id = orderItem.exampleId?.toString() || null

        basketAddToGA4(
          id,
          orderItem.priceSek,
          count,
          orderItem.discountPercentage,
          orderItem.signTypeKey,
          orderItem.sizeDisplay,
          orderItem.hangType,
          orderItem.materialNameForGtm,
          basketAddData.basketAdd.landingUrl
        )
        openBasket()
      }
    },
  })

  const handleClick = async () => {
    if (!minimatic) {
      return
    }

    if (!minimatic.state || !minimatic.state.fixmethod) {
      alert(t("toolbox.fix_method_required"))

      if (setFixmethodPickerOpen) {
        setFixmethodPickerOpen(true)
      }

      return
    }

    if (minimatic.state.objects.length === 0 && !warningShown.current && !minimatic.objectsDisabled) {
      warningShown.current = true
      if (!confirm(t("toolbox.empty_sign"))) {
        return
      }
    }

    const code = await minimatic.signcode()

    if (!code) {
      console.error("Failed to generate sign code")
      return
    }

    code.count = count || 1
    code.sign_category_id = signCategoryId || null
    code.example_id = Number(signId) || null
    code.exampler_id = examplerId || null

    // Convert signcode text objects to center alignment
    if (code.fabric_json.objects) {
      code.fabric_json.objects.forEach((obj: any) => {
        if (obj.type === "i-text" && obj.left && obj.width) {
          if (obj.textAlign === "right") {
            obj.left = obj.left - obj.width / 2
          } else if (obj.textAlign === "left") {
            obj.left = obj.left + obj.width / 2
          }
          obj.originX = "center"
        }
      })
    }

    const product = JSON.stringify(code)

    await mutation({ variables: { product } })
  }

  const loading = disabled || result.loading

  return (
    <button
      id="add_to_cart"
      className="minimatic button button-emphasis button-large button-large mb-0 ml-0 xl:flex-1"
      onClick={handleClick}
      disabled={loading}
      data-gtm="add-to-cart-button"
      type="button"
    >
      {loading ? (
        <LoadingAnimation />
      ) : (
        <span className="flex items-center justify-center">
          <CartFill />
          {t("signomatic.add_to_basket")}
        </span>
      )}
    </button>
  )
}
